import React, { useState } from 'react';
import { BrainCircuit, TrendingUp, Award, Zap, Clock, Users, ArrowUpRight, Video, Image, MessageCircle, DollarSign, Calendar, Lock, UserCheck, Heart, Star, Gift, Camera, Sparkles, ChevronRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
export function PredictiveCoachingSection() {
  const [activeTab, setActiveTab] = useState('suggestions');
  // Sample data for AI coach suggestions
  const coachSuggestions = [{
    id: '1',
    title: 'Upload more video content',
    description: 'Your subscribers engage 40% more with reels and videos than with photos.',
    priority: 'high',
    impact: 'engagement',
    action: 'Record a short video today',
    completionTime: '15 minutes',
    completed: false
  }, {
    id: '2',
    title: 'Mention your fan club more often',
    description: "25 users clicked on locked posts this week but didn't subscribe.",
    priority: 'medium',
    impact: 'revenue',
    action: 'Add fan club CTA to next 3 posts',
    completionTime: '5 minutes',
    completed: false
  }, {
    id: '3',
    title: 'Respond to unread messages',
    description: 'You have 12 unread messages from high-value fans waiting for response.',
    priority: 'high',
    impact: 'retention',
    action: 'Reply to VIP messages',
    completionTime: '20 minutes',
    completed: false
  }, {
    id: '4',
    title: 'Schedule content for peak hours',
    description: 'Posting between 8-10pm increases engagement by 35%.',
    priority: 'medium',
    impact: 'reach',
    action: 'Schedule next 3 posts for peak hours',
    completionTime: '10 minutes',
    completed: false
  }, {
    id: '5',
    title: 'Create a special weekend offer',
    description: 'Weekend promotions convert 28% better than weekday offers.',
    priority: 'medium',
    impact: 'revenue',
    action: 'Set up a weekend special',
    completionTime: '15 minutes',
    completed: false
  }];
  const achievements = [{
    id: '1',
    title: 'Content Creator',
    description: 'Post 5 days in a row',
    progress: 3,
    total: 5,
    reward: '50 tokens',
    icon: <Camera className="w-5 h-5 text-pink-500" data-id="element-5297" />
  }, {
    id: '2',
    title: 'Engagement Queen',
    description: 'Reply to 20 fan messages',
    progress: 15,
    total: 20,
    reward: '100 tokens',
    icon: <MessageCircle className="w-5 h-5 text-purple-500" data-id="element-5298" />
  }, {
    id: '3',
    title: 'Video Star',
    description: 'Upload 3 videos this week',
    progress: 1,
    total: 3,
    reward: '150 tokens',
    icon: <Video className="w-5 h-5 text-blue-500" data-id="element-5299" />
  }, {
    id: '4',
    title: 'Conversion Master',
    description: 'Convert 5 free followers to paid',
    progress: 2,
    total: 5,
    reward: '200 tokens',
    icon: <UserCheck className="w-5 h-5 text-green-500" data-id="element-5300" />
  }, {
    id: '5',
    title: 'Intimacy Expert',
    description: 'Reach 80% emotional bond with 10 fans',
    progress: 6,
    total: 10,
    reward: '250 tokens',
    icon: <Heart className="w-5 h-5 text-red-500" data-id="element-5301" />
  }];
  const weeklyInsights = {
    revenue: {
      current: 1250,
      previous: 980,
      change: '+27.6%'
    },
    newSubscribers: {
      current: 48,
      previous: 35,
      change: '+37.1%'
    },
    contentViews: {
      current: 12540,
      previous: 9870,
      change: '+27.1%'
    },
    messageResponses: {
      current: 85,
      previous: 92,
      change: '-7.6%'
    },
    topPerforming: [{
      type: 'video',
      title: 'Shower routine video',
      views: 2450,
      engagement: '68%'
    }, {
      type: 'photo',
      title: 'New lingerie photoshoot',
      views: 1980,
      engagement: '54%'
    }, {
      type: 'voice',
      title: 'Bedtime voice message',
      views: 1540,
      engagement: '72%'
    }],
    lowPerforming: [{
      type: 'photo',
      title: 'Casual outfit post',
      views: 340,
      engagement: '12%'
    }, {
      type: 'text',
      title: 'Good morning message',
      views: 420,
      engagement: '8%'
    }]
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 bg-red-100 dark:bg-red-900/30';
      case 'medium':
        return 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30';
      case 'low':
        return 'text-blue-500 bg-blue-100 dark:bg-blue-900/30';
      default:
        return 'text-gray-500 bg-gray-100 dark:bg-gray-900/30';
    }
  };
  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'engagement':
        return <Users className="w-4 h-4 text-purple-500" data-id="element-5302" />;
      case 'revenue':
        return <DollarSign className="w-4 h-4 text-green-500" data-id="element-5303" />;
      case 'retention':
        return <Heart className="w-4 h-4 text-pink-500" data-id="element-5304" />;
      case 'reach':
        return <TrendingUp className="w-4 h-4 text-blue-500" data-id="element-5305" />;
      default:
        return <Zap className="w-4 h-4 text-yellow-500" data-id="element-5306" />;
    }
  };
  return <div className="space-y-6" data-id="element-5307">
      {/* AI Coach Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-xl shadow-sm border border-blue-100 dark:border-blue-800/30" data-id="element-5308">
        <div className="flex items-center gap-4" data-id="element-5309">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center" data-id="element-5310">
            <BrainCircuit className="w-8 h-8 text-white" data-id="element-5311" />
          </div>
          <div data-id="element-5312">
            <h2 className="text-xl font-bold text-blue-800 dark:text-blue-300" data-id="element-5313">
              AI Success Coach
            </h2>
            <p className="text-sm text-blue-700 dark:text-blue-400" data-id="element-5314">
              Personalized guidance to grow your audience and income
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-5315">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg flex items-center gap-3" data-id="element-5316">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400" data-id="element-5317">
              <TrendingUp className="w-5 h-5" data-id="element-5318" />
            </div>
            <div data-id="element-5319">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5320">
                This Week's Revenue
              </p>
              <div className="flex items-center gap-1" data-id="element-5321">
                <p className="font-bold" data-id="element-5322">${weeklyInsights.revenue.current}</p>
                <span className="text-xs text-green-500" data-id="element-5323">
                  {weeklyInsights.revenue.change}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg flex items-center gap-3" data-id="element-5324">
            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400" data-id="element-5325">
              <Users className="w-5 h-5" data-id="element-5326" />
            </div>
            <div data-id="element-5327">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5328">
                New Subscribers
              </p>
              <div className="flex items-center gap-1" data-id="element-5329">
                <p className="font-bold" data-id="element-5330">
                  {weeklyInsights.newSubscribers.current}
                </p>
                <span className="text-xs text-green-500" data-id="element-5331">
                  {weeklyInsights.newSubscribers.change}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg flex items-center gap-3" data-id="element-5332">
            <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400" data-id="element-5333">
              <Award className="w-5 h-5" data-id="element-5334" />
            </div>
            <div data-id="element-5335">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5336">
                Creator Ranking
              </p>
              <div className="flex items-center gap-1" data-id="element-5337">
                <p className="font-bold" data-id="element-5338">Top 5%</p>
                <span className="text-xs text-green-500" data-id="element-5339">+2%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden" data-id="element-5340">
        <div className="flex border-b border-gray-200 dark:border-gray-700" data-id="element-5341">
          <button className={`flex-1 py-3 text-center font-medium text-sm relative ${activeTab === 'suggestions' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('suggestions')} data-id="element-5342">
            Coach Suggestions
            {activeTab === 'suggestions' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" data-id="element-5343"></div>}
          </button>
          <button className={`flex-1 py-3 text-center font-medium text-sm relative ${activeTab === 'achievements' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('achievements')} data-id="element-5344">
            Achievements & Goals
            {activeTab === 'achievements' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" data-id="element-5345"></div>}
          </button>
          <button className={`flex-1 py-3 text-center font-medium text-sm relative ${activeTab === 'insights' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('insights')} data-id="element-5346">
            Weekly Insights
            {activeTab === 'insights' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" data-id="element-5347"></div>}
          </button>
        </div>
        <div className="p-4" data-id="element-5348">
          {activeTab === 'suggestions' && <div className="space-y-4" data-id="element-5349">
              <div className="flex justify-between items-center" data-id="element-5350">
                <h3 className="font-medium" data-id="element-5351">AI Coach Suggestions</h3>
                <div className="flex items-center gap-2" data-id="element-5352">
                  <span className="text-xs text-gray-500" data-id="element-5353">Sort by:</span>
                  <select className="text-xs bg-gray-100 dark:bg-gray-700 rounded-md px-2 py-1" data-id="element-5354">
                    <option data-id="element-5355">Priority</option>
                    <option data-id="element-5356">Impact</option>
                    <option data-id="element-5357">Time Required</option>
                  </select>
                </div>
              </div>
              <div className="space-y-3" data-id="element-5358">
                {coachSuggestions.map(suggestion => <div key={suggestion.id} className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm" data-id="element-5359">
                    <div className="flex justify-between items-start" data-id="element-5360">
                      <div className="flex items-start gap-3" data-id="element-5361">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-5362">
                          <Zap className="w-5 h-5" data-id="element-5363" />
                        </div>
                        <div data-id="element-5364">
                          <h4 className="font-medium text-sm" data-id="element-5365">
                            {suggestion.title}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-5366">
                            {suggestion.description}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getPriorityColor(suggestion.priority)}`} data-id="element-5367">
                        {suggestion.priority} priority
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-3 pl-12" data-id="element-5368">
                      <div className="flex items-center gap-3" data-id="element-5369">
                        <div className="flex items-center gap-1" data-id="element-5370">
                          {getImpactIcon(suggestion.impact)}
                          <span className="text-xs capitalize" data-id="element-5371">
                            {suggestion.impact}
                          </span>
                        </div>
                        <div className="flex items-center gap-1" data-id="element-5372">
                          <Clock className="w-4 h-4 text-gray-500" data-id="element-5373" />
                          <span className="text-xs" data-id="element-5374">
                            {suggestion.completionTime}
                          </span>
                        </div>
                      </div>
                      <button className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium flex items-center gap-1" data-id="element-5375">
                        {suggestion.action}
                        <ChevronRight className="w-3 h-3" data-id="element-5376" />
                      </button>
                    </div>
                  </div>)}
              </div>
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-5377">
                <h4 className="font-medium text-sm mb-2" data-id="element-5378">
                  Looking for more suggestions?
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-5379">
                  <button className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-xs flex items-center justify-center gap-1" data-id="element-5380">
                    <Video className="w-4 h-4 text-red-500" data-id="element-5381" />
                    Content Ideas
                  </button>
                  <button className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-xs flex items-center justify-center gap-1" data-id="element-5382">
                    <DollarSign className="w-4 h-4 text-green-500" data-id="element-5383" />
                    Monetization Tips
                  </button>
                  <button className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-xs flex items-center justify-center gap-1" data-id="element-5384">
                    <Users className="w-4 h-4 text-blue-500" data-id="element-5385" />
                    Audience Growth
                  </button>
                </div>
              </div>
            </div>}
          {activeTab === 'achievements' && <div className="space-y-4" data-id="element-5386">
              <div className="flex justify-between items-center" data-id="element-5387">
                <h3 className="font-medium" data-id="element-5388">Achievements & Goals</h3>
                <button className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-md text-xs" data-id="element-5389">
                  View All
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-5390">
                {achievements.map(achievement => <div key={achievement.id} className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5391">
                    <div className="flex items-start gap-3" data-id="element-5392">
                      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center" data-id="element-5393">
                        {achievement.icon}
                      </div>
                      <div className="flex-1" data-id="element-5394">
                        <div className="flex justify-between items-start" data-id="element-5395">
                          <h4 className="font-medium text-sm" data-id="element-5396">
                            {achievement.title}
                          </h4>
                          <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full" data-id="element-5397">
                            {achievement.reward}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-5398">
                          {achievement.description}
                        </p>
                        <div className="mt-2" data-id="element-5399">
                          <div className="flex justify-between text-xs mb-1" data-id="element-5400">
                            <span className="text-gray-500" data-id="element-5401">Progress</span>
                            <span data-id="element-5402">
                              {achievement.progress}/{achievement.total}
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-5403">
                            <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" style={{
                        width: `${achievement.progress / achievement.total * 100}%`
                      }} data-id="element-5404"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
              {/* Milestone Tracking */}
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-100 dark:border-purple-800/30" data-id="element-5405">
                <div className="flex items-center justify-between mb-4" data-id="element-5406">
                  <div className="flex items-center gap-2" data-id="element-5407">
                    <Star className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-5408" />
                    <h3 className="font-medium" data-id="element-5409">Milestone Tracking</h3>
                  </div>
                  <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full" data-id="element-5410">
                    3 milestones active
                  </span>
                </div>
                <div className="space-y-3" data-id="element-5411">
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-5412">
                    <div className="flex justify-between items-center mb-2" data-id="element-5413">
                      <h4 className="font-medium text-sm" data-id="element-5414">
                        $10,000 Monthly Revenue
                      </h4>
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full" data-id="element-5415">
                        $2,540 to go
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5416">
                      <div className="h-full bg-gradient-to-r from-green-500 to-teal-500 rounded-full" style={{
                    width: '74.6%'
                  }} data-id="element-5417"></div>
                    </div>
                    <div className="flex justify-between text-xs mt-1" data-id="element-5418">
                      <span className="text-gray-500" data-id="element-5419">Current: $7,460</span>
                      <span className="text-gray-500" data-id="element-5420">Goal: $10,000</span>
                    </div>
                  </div>
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-5421">
                    <div className="flex justify-between items-center mb-2" data-id="element-5422">
                      <h4 className="font-medium text-sm" data-id="element-5423">5,000 Subscribers</h4>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full" data-id="element-5424">
                        2,600 to go
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5425">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{
                    width: '48%'
                  }} data-id="element-5426"></div>
                    </div>
                    <div className="flex justify-between text-xs mt-1" data-id="element-5427">
                      <span className="text-gray-500" data-id="element-5428">Current: 2,400</span>
                      <span className="text-gray-500" data-id="element-5429">Goal: 5,000</span>
                    </div>
                  </div>
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-lg" data-id="element-5430">
                    <div className="flex justify-between items-center mb-2" data-id="element-5431">
                      <h4 className="font-medium text-sm" data-id="element-5432">100 VIP Members</h4>
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full" data-id="element-5433">
                        32 to go
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5434">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{
                    width: '68%'
                  }} data-id="element-5435"></div>
                    </div>
                    <div className="flex justify-between text-xs mt-1" data-id="element-5436">
                      <span className="text-gray-500" data-id="element-5437">Current: 68</span>
                      <span className="text-gray-500" data-id="element-5438">Goal: 100</span>
                    </div>
                  </div>
                </div>
                <button className="w-full mt-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium" data-id="element-5439">
                  Set New Milestone
                </button>
              </div>
            </div>}
          {activeTab === 'insights' && <div className="space-y-4" data-id="element-5440">
              <div className="flex justify-between items-center" data-id="element-5441">
                <h3 className="font-medium" data-id="element-5442">Weekly Performance Digest</h3>
                <button className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium" data-id="element-5443">
                  Download Report
                </button>
              </div>
              {/* Performance Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-5444">
                <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5445">
                  <div className="flex items-center justify-between mb-1" data-id="element-5446">
                    <h4 className="text-xs text-gray-500" data-id="element-5447">Revenue</h4>
                    <span className={`text-xs ${weeklyInsights.revenue.change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`} data-id="element-5448">
                      {weeklyInsights.revenue.change}
                    </span>
                  </div>
                  <p className="text-lg font-bold" data-id="element-5449">
                    ${weeklyInsights.revenue.current}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-5450">
                    vs ${weeklyInsights.revenue.previous} last week
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5451">
                  <div className="flex items-center justify-between mb-1" data-id="element-5452">
                    <h4 className="text-xs text-gray-500" data-id="element-5453">New Subscribers</h4>
                    <span className={`text-xs ${weeklyInsights.newSubscribers.change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`} data-id="element-5454">
                      {weeklyInsights.newSubscribers.change}
                    </span>
                  </div>
                  <p className="text-lg font-bold" data-id="element-5455">
                    {weeklyInsights.newSubscribers.current}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-5456">
                    vs {weeklyInsights.newSubscribers.previous} last week
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5457">
                  <div className="flex items-center justify-between mb-1" data-id="element-5458">
                    <h4 className="text-xs text-gray-500" data-id="element-5459">Content Views</h4>
                    <span className={`text-xs ${weeklyInsights.contentViews.change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`} data-id="element-5460">
                      {weeklyInsights.contentViews.change}
                    </span>
                  </div>
                  <p className="text-lg font-bold" data-id="element-5461">
                    {weeklyInsights.contentViews.current}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-5462">
                    vs {weeklyInsights.contentViews.previous} last week
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5463">
                  <div className="flex items-center justify-between mb-1" data-id="element-5464">
                    <h4 className="text-xs text-gray-500" data-id="element-5465">Message Responses</h4>
                    <span className={`text-xs ${weeklyInsights.messageResponses.change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`} data-id="element-5466">
                      {weeklyInsights.messageResponses.change}
                    </span>
                  </div>
                  <p className="text-lg font-bold" data-id="element-5467">
                    {weeklyInsights.messageResponses.current}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-5468">
                    vs {weeklyInsights.messageResponses.previous} last week
                  </p>
                </div>
              </div>
              {/* Top & Low Performing Content */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5469">
                <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5470">
                  <div className="flex items-center gap-2 mb-3" data-id="element-5471">
                    <CheckCircle className="w-5 h-5 text-green-500" data-id="element-5472" />
                    <h4 className="font-medium" data-id="element-5473">Top Performing Content</h4>
                  </div>
                  <div className="space-y-3" data-id="element-5474">
                    {weeklyInsights.topPerforming.map((content, index) => <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-5475">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-5476">
                          {content.type === 'video' && <Video className="w-4 h-4 text-red-500" data-id="element-5477" />}
                          {content.type === 'photo' && <Image className="w-4 h-4 text-blue-500" data-id="element-5478" />}
                          {content.type === 'voice' && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-purple-500" data-id="element-5479">
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" data-id="element-5480" />
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2" data-id="element-5481" />
                              <line x1="12" y1="19" x2="12" y2="23" data-id="element-5482" />
                              <line x1="8" y1="23" x2="16" y2="23" data-id="element-5483" />
                            </svg>}
                        </div>
                        <div className="flex-1" data-id="element-5484">
                          <p className="text-sm font-medium" data-id="element-5485">{content.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-5486">
                            <span data-id="element-5487">{content.views} views</span>
                            <span data-id="element-5488">•</span>
                            <span className="text-green-500" data-id="element-5489">
                              {content.engagement} engagement
                            </span>
                          </div>
                        </div>
                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full" data-id="element-5490">
                          <Sparkles className="w-4 h-4 text-yellow-500" data-id="element-5491" />
                        </button>
                      </div>)}
                  </div>
                  <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800/30" data-id="element-5492">
                    <p className="text-xs text-green-700 dark:text-green-400" data-id="element-5493">
                      <span className="font-medium" data-id="element-5494">AI Insight:</span> Videos
                      showing your daily routine perform 45% better than other
                      content types. Consider creating more of this content.
                    </p>
                  </div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-5495">
                  <div className="flex items-center gap-2 mb-3" data-id="element-5496">
                    <XCircle className="w-5 h-5 text-red-500" data-id="element-5497" />
                    <h4 className="font-medium" data-id="element-5498">Low Performing Content</h4>
                  </div>
                  <div className="space-y-3" data-id="element-5499">
                    {weeklyInsights.lowPerforming.map((content, index) => <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-5500">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-5501">
                          {content.type === 'photo' && <Image className="w-4 h-4 text-blue-500" data-id="element-5502" />}
                          {content.type === 'text' && <MessageCircle className="w-4 h-4 text-gray-500" data-id="element-5503" />}
                        </div>
                        <div className="flex-1" data-id="element-5504">
                          <p className="text-sm font-medium" data-id="element-5505">{content.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-5506">
                            <span data-id="element-5507">{content.views} views</span>
                            <span data-id="element-5508">•</span>
                            <span className="text-red-500" data-id="element-5509">
                              {content.engagement} engagement
                            </span>
                          </div>
                        </div>
                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full" data-id="element-5510">
                          <AlertCircle className="w-4 h-4 text-red-500" data-id="element-5511" />
                        </button>
                      </div>)}
                  </div>
                  <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800/30" data-id="element-5512">
                    <p className="text-xs text-red-700 dark:text-red-400" data-id="element-5513">
                      <span className="font-medium" data-id="element-5514">AI Insight:</span> Casual
                      content without suggestive elements performs 78% worse.
                      Try adding subtle teasing elements to increase engagement.
                    </p>
                  </div>
                </div>
              </div>
              {/* Improvement Suggestions */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30" data-id="element-5515">
                <div className="flex items-center gap-2 mb-3" data-id="element-5516">
                  <BrainCircuit className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-5517" />
                  <h4 className="font-medium" data-id="element-5518">AI Improvement Suggestions</h4>
                </div>
                <div className="space-y-2" data-id="element-5519">
                  <div className="flex items-start gap-2" data-id="element-5520">
                    <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs mt-0.5" data-id="element-5521">
                      1
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-400" data-id="element-5522">
                      <span className="font-medium" data-id="element-5523">
                        Respond faster to messages
                      </span>{' '}
                      - Your response time has increased by 15%. Faster
                      responses lead to 35% higher retention.
                    </p>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5524">
                    <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs mt-0.5" data-id="element-5525">
                      2
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-400" data-id="element-5526">
                      <span className="font-medium" data-id="element-5527">
                        Create more video content
                      </span>{' '}
                      - Your video posts have 68% higher engagement than photos.
                      Aim for at least 3 videos per week.
                    </p>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-5528">
                    <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs mt-0.5" data-id="element-5529">
                      3
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-400" data-id="element-5530">
                      <span className="font-medium" data-id="element-5531">
                        Promote your VIP content more
                      </span>{' '}
                      - 25 users viewed locked content but didn't subscribe. Add
                      clearer calls-to-action.
                    </p>
                  </div>
                </div>
                <button className="w-full mt-3 py-2 bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium" data-id="element-5532">
                  Generate Detailed Improvement Plan
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}